/* Copyright (c) 2022-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  UpdateNotificationGroupInput,
  useUpdateNotificationGroupMutation,
} from "~/generated/graphql";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { useNotificationGroup } from "~/views/notificationGroups/notificationGroupDetails/NotificationGroupProvider";

type Props = {
  onSuccess: () => void;
};

export function useUpdateNotificationGroup({ onSuccess }: Props) {
  const { data: notificationGroup } = useNotificationGroup();

  const [updateNotificationGroupMutation, { loading }] =
    useUpdateNotificationGroupMutation({
      onError: (error) => {
        showError(error.message);
      },
      onCompleted: () => {
        onSuccess();
        showSuccess(
          generateActionSuccessText("Notification Group")()("updated")()
        );
      },
      update(cache, { data }, { variables }) {
        if (data?.updateNotificationGroup && variables?.input) {
          cache.modify({
            fields: {
              notificationGroup: (existing) => {
                return {
                  notificationGroup: [existing, variables.input],
                };
              },
            },
          });

          cache.gc();
        }
      },
    });

  function updateNotificationGroup(
    data: Partial<UpdateNotificationGroupInput>
  ) {
    notificationGroup &&
      updateNotificationGroupMutation({
        variables: {
          input: {
            ...data,
            id: notificationGroup?.id ?? "",
          },
        },
      });
  }

  return [updateNotificationGroup, loading] as const;
}
